import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from 'common/ui/button';
import { Input } from 'common/ui/input';
import { Label } from 'common/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'common/ui/select';
import { Controller, useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { sanitizeFormData } from 'utils/filter-form';
import { z } from 'zod';

// Field configuration types
export interface SelectOption {
  value: string | number;
  label: string;
}

export interface FilterFieldConfig {
  name: string;
  type: 'text' | 'select';
  label: string;
  placeholder?: string;
  minWidth?: string;
  options?: SelectOption[];
  defaultValue?: string | number;
  validation?: z.ZodString;
}

export interface DynamicFilterFormProps {
  fields?: FilterFieldConfig[];
  onSubmit?: (data: Record<string, any>) => void;
  onReset?: () => void;
  className?: string;
  showSearchField?: boolean;
  searchFieldConfig?: Partial<FilterFieldConfig>;
}

export const DynamicFilterForm = ({
  fields = [],
  onSubmit,
  onReset,
  className = '',
  showSearchField = true,
  searchFieldConfig = {},
}: DynamicFilterFormProps) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Extract current search params
  const currentParams = Object.fromEntries(searchParams.entries());

  // Default search field configuration
  const defaultSearchField: FilterFieldConfig = {
    name: 'search',
    type: 'text',
    label: 'Search',
    placeholder: 'Search...',
    minWidth: '200px',
    defaultValue: '',
    ...searchFieldConfig,
  };

  // Combine search field with other fields
  // If fields is undefined/empty and showSearchField is true, only show search field
  // If fields is undefined/empty and showSearchField is false, show nothing
  const allFields = showSearchField ? [defaultSearchField, ...fields] : fields;

  // Build Zod schema dynamically
  const schemaFields: Record<string, z.ZodTypeAny> = {};
  allFields.forEach(field => {
    schemaFields[field.name] = field.validation || z.string().optional();
  });
  const filterSchema = z.object(schemaFields);

  // Build default values with proper undefined handling
  const defaultValues: Record<string, string> = {};
  allFields.forEach(field => {
    const currentValue = currentParams[field.name];
    const defaultValue = field.defaultValue;

    // Handle undefined values properly
    if (currentValue !== undefined && currentValue !== null) {
      defaultValues[field.name] = String(currentValue);
    } else if (defaultValue !== undefined && defaultValue !== null) {
      defaultValues[field.name] = String(defaultValue);
    } else {
      defaultValues[field.name] = '';
    }
  });

  const { control, handleSubmit, reset } = useForm({
    resolver: zodResolver(filterSchema),
    defaultValues,
  });

  const handleFormSubmit = (data: Record<string, any>) => {
    console.log(data);

    const { page, ...otherParams } = currentParams || {};
    const sanitizedData = sanitizeFormData({ ...otherParams, ...data });

    setSearchParams(sanitizedData);
    onSubmit?.(sanitizedData);
  };

  const handleFormReset = () => {
    const resetValues: Record<string, string> = {};
    allFields.forEach(field => {
      const defaultValue = field.defaultValue;
      resetValues[field.name] =
        defaultValue !== undefined && defaultValue !== null
          ? String(defaultValue)
          : '';
    });
    reset(resetValues);
    setSearchParams({});
    onReset?.();
  };

  const renderField = (field: FilterFieldConfig) => {
    return (
      <div
        key={field.name}
        className="flex flex-col gap-1"
        style={{ minWidth: field.minWidth || '120px' }}
      >
        <Label htmlFor={field.name}>{field.label}</Label>
        <Controller
          name={field.name}
          control={control}
          render={({ field: controllerField }) => {
            if (field.type === 'text') {
              return (
                <Input
                  type="text"
                  id={field.name}
                  placeholder={field.placeholder || 'Search...'}
                  value={controllerField.value ?? ''}
                  onChange={e => controllerField.onChange(e.target.value ?? '')}
                />
              );
            }

            if (field.type === 'select') {
              return (
                <Select
                  onValueChange={value =>
                    controllerField.onChange(value ?? 'all')
                  }
                  value={controllerField.value ?? 'all'}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={field.placeholder || 'Select...'}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    {field.options?.map(option => (
                      <SelectItem
                        key={option.value}
                        value={option.value?.toString()}
                      >
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              );
            }

            return null;
          }}
        />
      </div>
    );
  };

  // Don't render anything if no fields to show
  if (allFields.length === 0) {
    return null;
  }

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className={className}>
      <div className="flex flex-wrap gap-2">
        {allFields.map(renderField)}

        <div className="flex gap-1 self-end">
          <Button type="submit">Filter</Button>
          <Button type="button" variant="outline" onClick={handleFormReset}>
            Reset
          </Button>
        </div>
      </div>
    </form>
  );
};
